import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {
  DateRange,
  Flex,
  FlexColumn,
  FlexSpace,
  Header2,
  LinksContainer,
  LinkTab,
  Modal
} from '@ubeya/shared-web/components';
import useEmployeeApplications from '@ubeya/shared/hooks/account/useEmployeeApplications';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { AccordionSummaryContainer } from '@ubeya/shared-web/components/Accordion/Accordion';
import { ReactComponent as FullScreenIcon } from '@ubeya/shared-web/assets/full-screen-open.svg';
import { ReactComponent as ExitFullScreenIcon } from '@ubeya/shared-web/assets/full-screen-close.svg';
import { RootWrapper } from '../styled';
import ApplicationTable from './ApplicationTable';
import { ApplicationsStatusCounts } from './ApplicationsStatusCounts';

const TABS = {
  applications: 'applications',
  rejections: 'rejections'
};

const Space = styled.div`
  margin-top: 16px;
`;

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 0 41px 40px;
  min-width: 500px;
`;

const ExitFullScreenIconButton = styled(ExitFullScreenIcon)`
  cursor: pointer;
`;

const HeaderContainer = styled(FlexSpace)`
  flex-direction: row;
`;

const TabHeaderContainer = styled(FlexSpace)`
  align-items: center;
  margin-bottom: 10px;
`;

const DateRangeContainer = styled(Flex)`
  align-items: center;
`;

const StatsCount = styled.span`
  font-size: 14px;
`;

const getDefaultTimeRange = () => ({
  start: moment().subtract(90, 'days').startOf('day'),
  end: moment().endOf('day')
});

const LinksWrapper = styled(FlexSpace)`
  flex-direction: row;
  margin-bottom: 16px;
`;

const ApplicationsAndRejections = ({ data: { id } }) => {
  const { t } = useContext(ConfigContext);
  const [isCardExpand, setIsCardExpand] = useState(false);
  const [timeRange, setTimeRange] = useState(getDefaultTimeRange);
  const [activeTab, setActiveTab] = useState(TABS.applications);

  const isRejectionsTab = activeTab === TABS.rejections;

  const { applications } = useEmployeeApplications({
    employeeId: id,
    timeRange,
    includeHistory: true,
    onlyRejected: isRejectionsTab,
    ...(isRejectionsTab && { includeRejectedApplications: true })
  });

  const count = (applications || []).filter(({ shift }) => !!shift).length;

  const handleCardExpand = useCallback(() => {
    setIsCardExpand(true);
  }, []);

  const onClose = useCallback(() => {
    setIsCardExpand(false);
  }, []);

  const handleTabClick = useCallback(
    (tabKey) => {
      if (tabKey === activeTab) {
        return;
      }
      setActiveTab(tabKey);
    },
    [activeTab]
  );

  const Content = useMemo(
    () => (
      <>
        {isCardExpand && (
          <HeaderContainer>
            <Header2>{t('applicationsAndRejections')}</Header2>
            <ExitFullScreenIconButton onClick={onClose} />
          </HeaderContainer>
        )}
        <LinksWrapper>
          <LinksContainer>
            <LinkTab isActive={activeTab === TABS.applications} onClick={() => handleTabClick(TABS.applications)}>
              {t('applications')}
            </LinkTab>
            <LinkTab isActive={activeTab === TABS.rejections} onClick={() => handleTabClick(TABS.rejections)}>
              {t('rejections')}
            </LinkTab>
          </LinksContainer>
        </LinksWrapper>
        <TabHeaderContainer>
          <DateRangeContainer>
            <DateRange value={timeRange} onChange={setTimeRange} numberOfMonths={1} />
            <StatsCount>({count})</StatsCount>
          </DateRangeContainer>

          {activeTab === TABS.applications && <ApplicationsStatusCounts applications={applications} />}
        </TabHeaderContainer>

        {activeTab === TABS.applications && <ApplicationTable employeeId={id} timeRange={timeRange} />}
        {activeTab === TABS.rejections && <ApplicationTable employeeId={id} timeRange={timeRange} showRejectedOnly />}
      </>
    ),
    [activeTab, count, handleTabClick, id, isCardExpand, onClose, t, timeRange, applications]
  );

  return (
    <RootWrapper>
      <Wrapper>
        <AccordionSummaryContainer>
          <Header2>{t('applicationsAndRejections')}</Header2>
          <FullScreenIcon onClick={handleCardExpand} />
        </AccordionSummaryContainer>
        {isCardExpand ? (
          <Modal onClose={onClose} showConfirmButton={false} size="xxlarge">
            {Content}
          </Modal>
        ) : (
          <>
            <Space />
            {Content}
          </>
        )}
      </Wrapper>
    </RootWrapper>
  );
};

export default React.memo(ApplicationsAndRejections);
